import type { FishSpeciesId } from './fish';
import { mix32 } from './streams';
import { SLOPE_PER_REEF } from './data/visits/slope';

/**
 * The animals that visit a habitat other than their own (M147, the user's word: "make it so that each sea creature can
 * have a chance to appear in other habitats as well, as long as it is scientifically realistic. also, allow players to
 * sometimes follow an animal into a different habitat if it happens to be travelling there"), as the sources measure
 * them: how many of a species are in each habitat it visits, against its own, and when in the day one crosses between
 * them. The honu's and the monk seals' visits are M145's (`src/render/habitats/data.ts`). Data the drawing reads
 * (the aquarium session's), with no state of its own: each visitor the drawing's day decides, each figure here its source.
 */

/** A habitat at Maui Nui by its depths: the Reef's floor 0 to 30 m, the Slope's 30 to 400 m (since M215; 30 to 150 m before), the open water near the surface, the Twilight's 200 to 1,000 m, the Deep's below. */
export type VisitHabitat = 'reef' | 'slope' | 'blue' | 'twilight' | 'deep';

/** An animal that visits: a reef fish, or a humpback. */
export type VisitorSpecies = FishSpeciesId | 'humpback';

/**
 * How many of a species a habitat it visits holds, against its own habitat: `perAreaOfHome`, its animals a square meter
 * there for each a square meter at home, at any moment (the counts are snapshots: divers' counts for the fish, groups
 * seen at the surface for the humpback). A scene's expected count of a visitor is `value` times its home's animals a
 * square meter (the fish: the reef's own, as its hectare holds them; the humpback: the whales a square meter of the
 * open water they use) times the scene's area; its whole count on a day `visitorCountOn`'s, at every hour, each one there
 * swapped once a day (`crossingOf`).
 */
export interface HabitatUse {
  habitat: VisitHabitat;
  measure: 'perAreaOfHome';
  value: number;
  source: string;
}

/** A species' own habitat and the others it visits, and why none other. */
export interface SpeciesVisits {
  home: VisitHabitat;
  visits: readonly HabitatUse[];
  none: string;
}

const FUKUNAGA = 'Fukunaga, Kosaki, Wagner and Kane 2016, PLoS ONE 11: e0157861, S2 Text (CC0): mean fish a 100 m² over every site 30 to 67 m deep over every site shallower, Northwestern Hawaiian Islands, 2010 to 2012 (`npm run data:mesophotic`)';

/** A reef fish on the Slope at its measured density there over the reef's; none where none was counted. */
function reefFish(id: FishSpeciesId, none: string): SpeciesVisits {
  const value = SLOPE_PER_REEF[id] ?? 0;
  return { home: 'reef', visits: value > 0 ? [{ habitat: 'slope', measure: 'perAreaOfHome', value, source: FUKUNAGA }] : [], none };
}

/**
 * The humpbacks over the reef's depths a km² of sea for each over their own water's: "female-calf pairs in the Au'au
 * Channel avoided shallow waters (<20 m)" (Cartwright et al. 2012, PLoS ONE 7: e38004, CC BY, p. 1). Of its 148 groups
 * in the eastern ʻAuʻau Channel, March of 2008 to 2010, 1 (of 86 with a calf, Table 2; none of 62 of adults, Table 4)
 * was over its 10.2 km² of water shallower than 20 m and 147 over its other 114.3 km², 20 to 112 m (the tables' areas,
 * 124.5 km² in all): (1 / 10.2) / (147 / 114.3) = 0.0762 (MEASURED). Its home the water the game draws its whales in
 * (the Blue Water's scene), taken as the 20 to 112 m the groups were seen over (a reading: the scene stands for the whales'
 * water, not its floor's depth), its density `humpbacksPerM2`; groups a km² taken as whales a km² (a reading); the reef's 0 to 30 m by its band under
 * 20 m (the 20 to 40 m band's 8 groups straddle the reef's edge: a lower bound). Pack et al. 2018 (Marine Mammal Science
 * 34, Table 1) found 1 of 70 sightings of identified females off west Maui over water shallower than 20 m (a check, of
 * sightings, not groups), which disagrees: its own 1 sighting over 34.7 km² shallower than 20 m and 69 over the other
 * 1,252.2 km² give 0.52 (0.20 against its 20 to 80 m alone; MEASURED from its Table 1), 2.6 to 7 times Cartwright et al.'s,
 * its area mostly water deeper than 80 m and its one sighting a female without a calf (open). Of their time near the
 * bottom, on the Slope's floor, nothing was served (their dive studies on refused hosts).
 */
export const HUMPBACK_OVER_REEF_PER_AREA = (1 / 10.2) / ((86 + 62 - 1) / (124.5 - 10.2));

/**
 * Hawaiʻi's humpbacks' own water, their home's density's area: the main islands' insular shelf within the 200 m contour,
 * Kauaʻi's 923, Honolulu's 927, Maui's 3,641 and Hawaiʻi's 1,056 km² (Meyer et al. 2016, *Spatial Dynamics of Tiger
 * Sharks around Maui and Oahu*, report to DLNR, Table 1, p. 11, by county, Kauaʻi's with Niʻihau and Maui's with Molokaʻi, Lānaʻi and Kahoʻolawe; 6,547 km² in all), holding 90% of the islands'
 * whales: "Herman et al. (1980) and Mobley et al. (1999) showed that 90% of humpback whales in the Hawaiian Islands are
 * found within the 100-fathom isobath (i.e., 183 m contour of island shorelines)" (Pack et al. 2018, p. 14, compiled).
 * The 200 m contour taken for the 183 m, and the whales in Hawaiʻi's waters (the archipelago's EEZ, Becker et al. 2022's,
 * `whales.ts`) for the main islands' whose 90% Herman and Mobley counted (readings: any wintering in the Northwestern
 * islands put on the main islands' shelf, their share unmeasured here).
 */
const HUMPBACK_WATER_KM2 = 923 + 927 + 3_641 + 1_056;
/** The share of Hawaiʻi's humpbacks over that water (above). */
const HUMPBACK_SHARE_IN_WATER = 0.9;

/** The humpbacks a square meter of their own water, from the whales in Hawaiʻi's waters that day (`whaleCounts(...).inHawaii`): their home's density for `HabitatUse` (M147, the aquarium session's ask). */
export function humpbacksPerM2(inHawaii: number): number {
  return (HUMPBACK_SHARE_IN_WATER * inHawaii) / (HUMPBACK_WATER_KM2 * 1e6);
}

/**
 * Each visiting species' habitats. The reef fish on the Slope by Fukunaga et al.'s counts (`SLOPE_PER_REEF`); the
 * octopus, which they did not count, nowhere but the reef; the humpback over the reef by Cartwright et al.'s groups.
 */
export const SPECIES_VISITS: Readonly<Record<VisitorSpecies, SpeciesVisits>> = {
  bullethead: reefFish('bullethead', 'the open water and deeper: a fish of the reef\'s floor, to 34 m in the main islands (Nadon 2017, p. 169, compiled)'),
  palenose: reefFish('palenose', 'the open water and deeper: to 48 m in the main islands (Nadon 2017, p. 181, compiled); its Slope\'s density one site\'s'),
  roi: reefFish('roi', 'the Slope (none counted below 30 m at the Northwestern islands, where it is rare; recorded to 80 m in the main islands, Nadon 2017, p. 193, open) and deeper'),
  kala: reefFish('kala', 'the open water and deeper: recorded to 120 m (Nadon 2017, p. 69, compiled), below the Slope\'s counts; its Slope\'s density one site\'s'),
  ulua: reefFish('ulua', 'the open water and the Twilight: to 242 m off Hawaiʻi Island (Evans 2021, p. 22), its time there not measured as habitats'),
  omilu: reefFish('omilu', 'the open water and the Twilight: recorded to 230 m (Nadon 2017, p. 88, compiled), its time there not measured'),
  redlip: reefFish('redlip', 'the Slope (none counted below 30 m at the Northwestern islands; about a sixth of Maui Nui\'s region\'s on its 30 to 53 m by Asher, Williams and Harvey 2017\'s Figure 10, a figure only: open) and deeper'),
  nenue: reefFish('nenue', 'the Slope and deeper: none counted below 30 m at the Northwestern islands (0 of 82 sites), on 34 of their 118 reef sites'),
  moanokea: reefFish('moanokea', 'the open water and deeper: recorded to 113 m (Nadon 2017, p. 136, from Pyle et al. 2016), below the Slope\'s counts'),
  manini: reefFish('manini', 'the Slope and deeper: none counted below 30 m at the Northwestern islands (0 of 82 sites), on 85 of their 118 reef sites'),
  taape: reefFish('taape', 'the Slope and deeper: recorded to 265 m (Nadon 2017, p. 109, from Pyle et al. 2016), its numbers below 30 m not measured at Maui Nui (the Northwestern islands\' schools on 7 of 82 sites, where it is an introduced fish at its range\'s edge, not taken: open)'),
  octopus: { home: 'reef', visits: [], none: 'every other habitat: "from the intertidal zone to depths of 45 m" (Ivey 2007, p. 6, compiled), no count of it deeper than 30 m' },
  humpback: {
    home: 'blue',
    visits: [{ habitat: 'reef', measure: 'perAreaOfHome', value: HUMPBACK_OVER_REEF_PER_AREA, source: 'Cartwright et al. 2012, PLoS ONE 7: e38004 (CC BY), Tables 2 and 4: 1 group over 10.2 km² of water shallower than 20 m, 147 over 114.3 km² of 20 to 112 m' }],
    none: 'the Slope\'s floor, the Twilight and the Deep: its dives\' depths and time unread (refused hosts); "90% of humpback whales in the Hawaiian Islands are found within the 100-fathom isobath" (Pack et al. 2018, p. 14, compiled)',
  },
};

/** Hours of the day. */
const DAY_HOURS = 24;

/**
 * A visitor's swap on a day: each one a scene holds (its `slot`, 0 to its whole count on the day less one, `visitorCountOn`) goes back to its home
 * at `hour` and another of its kind comes from its home in its place at once, so the scene holds its measured count at
 * every hour (`HabitatUse`) and one of them can be followed home, or its replacement followed from home. The hour is drawn
 * evenly over the day, each slot's on its own draw: no diel pattern of the crossing is measured for any of them (the ulua
 * deeper by night by "generally < 5 m", Evans 2021, p. 43; the tiger sharks' visits to Maui's shallows "at all times of the
 * day and night", Meyer et al. 2018, p. 9), and no rate of their turnover, so one swap a slot a day (JUDGED). The same for
 * the same seed, day, species, habitat and slot however often it is asked.
 */
export interface Crossing { home: VisitHabitat; visited: VisitHabitat; hour: number }

/** A habitat's and a species' own numbers for the crossing's draws. */
function code(name: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < name.length; i++) h = Math.imul(h ^ name.charCodeAt(i), 0x01000193);
  return h >>> 0;
}

/** An even draw in [0, 1) for a day, a species, a habitat and a slot: no state, the same every time. */
function draw(seed: number, epochDay: number, species: VisitorSpecies, habitat: VisitHabitat, slot: number): number {
  return mix32(mix32(seed ^ code(species)) ^ Math.imul(epochDay + 1, 0x9e3779b1) ^ code(habitat) ^ Math.imul(slot + 1, 0x85ebca77)) / 4294967296;
}

/**
 * A scene's whole count of a visitor on a day, from its expected count (`HabitatUse`): the whole part every day and one
 * more on the share of days the rest gives, one even draw a day, as M145's honu and seals come or not by their odds (so a
 * scene expecting 0.15 holds one on about one day in seven, and its long-run mean is its expected count). The same all
 * day, however often it is asked; the draw its own (slot -1, apart from the swaps').
 */
export function visitorCountOn(expected: number, seed: number, epochDay: number, species: VisitorSpecies, habitat: VisitHabitat): number {
  if (!(expected > 0)) return 0;
  const whole = Math.floor(expected);
  return whole + (draw(seed, epochDay, species, habitat, -1) < expected - whole ? 1 : 0);
}

/** The day's swap of the visitor in slot `slot` of `species` in `habitat` (one it visits), on the world's `seed`; null where the species does not visit it. */
export function crossingOf(seed: number, epochDay: number, species: VisitorSpecies, habitat: VisitHabitat, slot: number): Crossing | null {
  const v = SPECIES_VISITS[species];
  if (habitat === v.home || !v.visits.some((u) => u.habitat === habitat)) return null;
  return { home: v.home, visited: habitat, hour: draw(seed, epochDay, species, habitat, slot) * DAY_HOURS };
}
